import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Chip,
    Description,
    FieldError,
    Label,
    ListBox,
    NumberField,
    Select,
    TextArea,
    TextField,
} from '@heroui/react';
import type { TaxRegime } from '@/api/models';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    formatBpsPercent,
    isOverride,
    MAX_RATE_PERCENT,
    RATE_PERCENT_DECIMALS,
    RATE_STEP_PERCENT,
    REGIME_OPTIONS,
    REGIME_SEEDED_FIELDS,
    regimeDefaultBps,
    regimeDescriptionKey,
    regimeLabelKey,
    STATUTORY_FIELDS,
    type RateField,
} from './settingsMeta';
import type { UseSettingsPageResult } from './useSettingsPage';

type SettingsPageViewProps = {
    page: UseSettingsPageResult;
    /**
     * Settings are still in flight. Headings, field labels and both CTAs stay
     * rendered; the inputs are disabled and the Save/Discard pair is already
     * inert because nothing is dirty yet.
     */
    isLoading: boolean;
};

/**
 * Render-only tax settings. The shell supplies the page title, so there is none
 * here and no page-root padding.
 *
 * The screen's job is to make four things unmissable:
 *
 * - **which regime is in force**, and what it implies;
 * - **which rates the regime fills in** versus which are statutory and
 *   unaffected by it — so switching regime is not a leap of faith about payroll;
 * - **which rates have been changed away from the regime's figure**, marked as
 *   such rather than presented as if the regime had set them;
 * - **when the change takes effect**, and why today may not be available.
 */
const RateInput: React.FC<{
    field: RateField;
    page: UseSettingsPageResult;
    isLoading: boolean;
}> = ({ field, page, isLoading }) => {
    const { t, i18n } = useTranslation('pages');
    const errorKey = page.errors[field.key];
    const value = page.draft[field.key];

    const overridden =
        field.isRegimeSeeded &&
        isOverride(
            page.regime,
            field.key as 'corporateIncomeTaxBps',
            value ?? null,
        );

    return (
        <NumberField
            // Deliberately NOT `style: 'percent'`. That puts the % inside the
            // editable text, so a caret after it turns "8%" into "8%79" on the
            // next keystroke. The symbol is a static suffix below instead, and
            // the field holds a plain 0–100 number.
            formatOptions={{
                style: 'decimal',
                maximumFractionDigits: RATE_PERCENT_DECIMALS,
            }}
            fullWidth
            isDisabled={isLoading}
            isInvalid={Boolean(errorKey)}
            isReadOnly={!page.canWrite}
            maxValue={MAX_RATE_PERCENT}
            minValue={0}
            onChange={(next) =>
                page.onRateChange(
                    field.key,
                    next === undefined || Number.isNaN(next) ? null : next,
                )
            }
            step={RATE_STEP_PERCENT}
            value={value ?? undefined}
            variant="secondary"
            {...getNodeTestIdProp(`settings-rate-${field.key}`)}
        >
            <span className="flex items-center justify-between gap-2">
                <Label>{t(field.labelKey)}</Label>
                {overridden ? (
                    <Chip
                        color="warning"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(`settings-override-${field.key}`)}
                    >
                        {t('settings.overridden')}
                    </Chip>
                ) : null}
            </span>
            <NumberField.Group className="w-full">
                <NumberField.DecrementButton />
                {/* `.number-field__group` is a three-column GRID
                    (`auto 1fr auto`), not a flex row — a fourth child wraps
                    onto a second row that `overflow: hidden` then clips, which
                    is how the increment button disappeared. So the unit shares
                    the middle cell with the input rather than taking a column
                    of its own. It stays outside the editable string (see
                    `settingsMeta`) and is left readable by assistive tech,
                    since the label alone doesn't say the value is a percentage. */}
                <span className="flex h-full min-w-0 items-center">
                    <NumberField.Input className="min-w-0 flex-1 text-right tabular-nums" />
                    <span className="pr-3 pl-1 text-sm text-muted">%</span>
                </span>
                <NumberField.IncrementButton />
            </NumberField.Group>
            {errorKey ? (
                <FieldError>{t(errorKey)}</FieldError>
            ) : (
                <Description>
                    {overridden
                        ? t('settings.regimeDefaultIs', {
                              value: formatBpsPercent(
                                  regimeDefaultBps(
                                      page.regime,
                                      field.key as 'corporateIncomeTaxBps',
                                  ),
                                  i18n.language,
                              ),
                          })
                        : t(field.affectsKey)}
                </Description>
            )}
        </NumberField>
    );
};

export const SettingsPageView: React.FC<SettingsPageViewProps> = ({
    page,
    isLoading,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('settings.title')}
            // Full width here, unlike its `/accounting` counterpart: inside the
            // Configurations shell the page sits under a tab bar that spans the
            // content area, and a 3xl column beneath a full-width bar reads as a
            // rendering mistake rather than a measure.
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('settings-section')}
        >
            {/* Why the page is read-only, stated rather than left to be
                inferred from a disabled button. */}
            {!page.canWrite ? (
                <div
                    className="flex items-start gap-2 rounded-xl bg-surface-secondary p-4"
                    {...getNodeTestIdProp('settings-read-only-notice')}
                >
                    <Icon
                        aria-hidden
                        className="mt-0.5 shrink-0 text-muted"
                        icon="gravity-ui:lock"
                    />
                    <p className="text-sm text-muted">
                        {t('settings.readOnlyReason')}
                    </p>
                </div>
            ) : null}

            {/* Rates exist but none is in force yet — a state that would
                otherwise read as an empty page. */}
            {page.status === 'scheduled' ? (
                <div
                    className="flex items-start gap-2 rounded-xl bg-surface-secondary p-4"
                    {...getNodeTestIdProp('settings-scheduled-notice')}
                >
                    <Icon
                        aria-hidden
                        className="mt-0.5 shrink-0 text-warning"
                        icon="gravity-ui:calendar"
                    />
                    <p className="text-sm text-muted">
                        {t('settings.scheduledOnly', {
                            date: page.latestSet?.effectiveFrom ?? '',
                        })}
                    </p>
                </div>
            ) : null}

            {/* Regime */}
            <div className="flex flex-col gap-3 rounded-xl bg-surface-secondary p-4">
                <div className="flex flex-col gap-1">
                    <h2 className="text-sm font-semibold text-foreground">
                        {t('settings.regimeHeading')}
                    </h2>
                    <p className="text-xs text-muted">
                        {t('settings.regimeIntro')}
                    </p>
                </div>

                <Select
                    className="w-full max-w-sm"
                    isDisabled={isLoading || !page.canWrite}
                    onChange={(next) =>
                        next && page.onRegimeChange(String(next) as TaxRegime)
                    }
                    value={page.regime}
                    variant="secondary"
                >
                    <Label>{t('settings.regimeLabel')}</Label>
                    <Select.Trigger {...getNodeTestIdProp('settings-regime')}>
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {REGIME_OPTIONS.map((regime) => (
                                <ListBox.Item
                                    id={regime}
                                    key={regime}
                                    textValue={t(regimeLabelKey(regime))}
                                >
                                    {t(regimeLabelKey(regime))}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>

                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('settings-regime-description')}
                >
                    {t(regimeDescriptionKey(page.regime))}
                </p>
            </div>

            {/* Regime-seeded rates — filled in by the regime, still editable */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="text-sm font-semibold text-foreground">
                        {t('settings.distributionHeading')}
                    </h2>
                    <p className="text-xs text-muted">
                        {t('settings.distributionIntro')}
                    </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {REGIME_SEEDED_FIELDS.map((field) => (
                        <RateInput
                            field={field}
                            isLoading={isLoading}
                            key={field.key}
                            page={page}
                        />
                    ))}
                </div>
            </div>

            {/* Statutory rates — explicitly not regime-driven */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="text-sm font-semibold text-foreground">
                        {t('settings.statutoryHeading')}
                    </h2>
                    <p className="text-xs text-muted">
                        {t('settings.statutoryIntro')}
                    </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {STATUTORY_FIELDS.map((field) => (
                        <RateInput
                            field={field}
                            isLoading={isLoading}
                            key={field.key}
                            page={page}
                        />
                    ))}
                </div>
            </div>

            {/* When it takes effect, and why */}
            {page.canWrite ? (
                <div className="flex flex-col gap-4 rounded-xl bg-surface-secondary p-4">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-sm font-semibold text-foreground">
                            {t('settings.changeHeading')}
                        </h2>
                        <p className="text-xs text-muted">
                            {t('settings.changeIntro')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="flex flex-col gap-1">
                            <DatePickerField
                                id="settings-effective-from"
                                isDisabled={isLoading}
                                isRequired
                                label={t('settings.effectiveFromLabel')}
                                minValue={
                                    page.earliestEffectiveFrom ?? undefined
                                }
                                name="effectiveFrom"
                                onChange={page.onEffectiveFromChange}
                                value={page.effectiveFrom}
                            />
                            {/* After a future-dated change, today is no longer
                                legal. Say so — an unexplained date is worse
                                than a constrained one. */}
                            {page.isTodayUnavailable ? (
                                <span
                                    className="text-xs text-muted"
                                    {...getNodeTestIdProp(
                                        'settings-effective-from-hint',
                                    )}
                                >
                                    {t('settings.effectiveFromConstrained', {
                                        date:
                                            page.latestSet?.effectiveFrom ?? '',
                                    })}
                                </span>
                            ) : null}
                        </div>

                        <TextField
                            className="flex flex-col gap-1"
                            isDisabled={isLoading}
                            onChange={page.onNoteChange}
                            value={page.note}
                            variant="secondary"
                            {...getNodeTestIdProp('settings-note')}
                        >
                            <Label>{t('settings.noteLabel')}</Label>
                            <TextArea
                                maxLength={page.noteMaxLength}
                                placeholder={t('settings.notePlaceholder')}
                                rows={2}
                                variant="secondary"
                            />
                            <Description>
                                {t('settings.noteHint', {
                                    count:
                                        page.noteMaxLength - page.note.length,
                                })}
                            </Description>
                        </TextField>
                    </div>
                </div>
            ) : null}

            {/* A save failure, with the one thing that fixes it */}
            {page.saveErrorKey ? (
                <div
                    className="flex flex-col items-start gap-2 rounded-xl bg-danger-soft p-4"
                    {...getNodeTestIdProp('settings-error')}
                >
                    <p className="text-sm text-danger-soft-foreground">
                        {t(page.saveErrorKey)}
                    </p>
                    {page.canReloadAndReapply ? (
                        <Button
                            onPress={page.onReloadAndReapply}
                            size="sm"
                            variant="outline"
                            {...getNodeTestIdProp('settings-reload-reapply')}
                        >
                            {t('settings.actions.reloadAndReapply')}
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {page.canWrite ? (
                <div className="flex items-center justify-end gap-2">
                    {page.isSaved && !page.isDirty ? (
                        <span
                            className="flex items-center gap-1.5 text-sm text-success"
                            {...getNodeTestIdProp('settings-saved')}
                        >
                            <Icon aria-hidden icon="gravity-ui:circle-check" />
                            {t('settings.saved')}
                        </span>
                    ) : null}
                    <Button
                        isDisabled={!page.isDirty}
                        onPress={page.onReset}
                        variant="tertiary"
                        {...getNodeTestIdProp('settings-reset')}
                    >
                        {t('settings.actions.discard')}
                    </Button>
                    <Button
                        isDisabled={!page.isDirty || !page.isValid}
                        isPending={page.isSaving}
                        onPress={page.onSave}
                        variant="primary"
                        {...getNodeTestIdProp('settings-save')}
                    >
                        {t('settings.actions.save')}
                    </Button>
                </div>
            ) : null}

            <p className="text-xs text-muted">{t('settings.footerNote')}</p>
        </section>
    );
};
